/**
 * LocationsPage — /settings/locations (admin, World 2).
 *
 * The Business→Branch→Location tree, made visible and editable. This is where a
 * chain operator sets up outlets: add a Branch (a physical outlet), then add
 * Locations under it (warehouse, shop-floor, kitchen…). The default branch and
 * location are protected; locations holding stock can't be deleted (the UI
 * disables it and explains why) — you transfer stock out first.
 *
 * Reads GET /locations/tree; mutates via the /locations/branches|locations
 * endpoints. Link to this page from business settings.
 */
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  Warehouse,
  Store,
  Plus,
  Star,
  Trash2,
  Pencil,
  Loader2,
  ChevronRight,
  ChevronDown,
  PackageOpen,
} from "lucide-react";
import api from "@/services/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const unwrap = (res: any) => res?.data?.data ?? res?.data;

const BRANCH_KINDS = [
  "outlet",
  "warehouse",
  "restaurant",
  "pharmacy",
  "office",
  "other",
];
const LOCATION_KINDS = [
  "warehouse",
  "store",
  "kitchen",
  "counter",
  "dispensary",
  "storeroom",
  "other",
];

interface LocationNode {
  _id: string;
  name: string;
  code: string;
  kind: string;
  isDefault: boolean;
  isActive: boolean;
  unitsOnHand: number;
  canDelete: boolean;
}
interface BranchNode extends Omit<LocationNode, "unitsOnHand" | "canDelete"> {
  address: string;
  phone: string;
  locations: LocationNode[];
  unitsOnHand: number;
  canDelete: boolean;
}

export default function LocationsPage() {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [dialog, setDialog] = useState<
    | { type: "branch"; mode: "create" | "edit"; node?: BranchNode }
    | {
        type: "location";
        mode: "create" | "edit";
        branchId: string;
        node?: LocationNode;
      }
    | null
  >(null);

  const { data: tree, isLoading } = useQuery({
    queryKey: ["locations", "tree"],
    queryFn: async () =>
      unwrap(await api.get("/locations/tree")) as BranchNode[],
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["locations", "tree"] });

  const mutate = (fn: () => Promise<any>, okMsg: string) =>
    fn()
      .then(() => {
        invalidate();
        toast.success(okMsg);
      })
      .catch((e) =>
        toast.error(
          e?.response?.data?.error ||
            e?.response?.data?.message ||
            "Something went wrong",
        ),
      );

  const delBranch = useMutation({
    mutationFn: (id: string) => api.delete(`/locations/branches/${id}`),
    onSuccess: () => {
      invalidate();
      toast.success("Branch removed");
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.error || "Can't remove this branch"),
  });
  const delLocation = useMutation({
    mutationFn: (id: string) => api.delete(`/locations/locations/${id}`),
    onSuccess: () => {
      invalidate();
      toast.success("Location removed");
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.error || "Can't remove this location"),
  });
  const makeDefaultBranch = useMutation({
    mutationFn: (id: string) => api.post(`/locations/branches/${id}/default`),
    onSuccess: () => {
      invalidate();
      toast.success("Default branch updated");
    },
  });
  const makeDefaultLocation = useMutation({
    mutationFn: (id: string) => api.post(`/locations/locations/${id}/default`),
    onSuccess: () => {
      invalidate();
      toast.success("Default location updated");
    },
  });

  const toggle = (id: string) =>
    setExpanded((e) => ({ ...e, [id]: !(e[id] ?? true) }));
  const isOpen = (id: string) => expanded[id] ?? true;

  return (
    <div className="mx-auto w-full max-w-4xl space-y-5">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Locations</h1>
          <p className="text-sm text-muted-foreground">
            Set up your branches and the stock locations inside them. Stock,
            sales, and staff are organised by branch.
          </p>
        </div>
        <Button onClick={() => setDialog({ type: "branch", mode: "create" })}>
          <Plus size={16} className="mr-1.5" /> Add branch
        </Button>
      </header>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !tree || tree.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border py-16 text-center">
          <Building2 className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            No branches yet. Add your first branch to get started.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {tree.map((branch) => (
            <div
              key={branch._id}
              className="rounded-lg border border-border bg-card"
            >
              {/* Branch row */}
              <div className="flex items-center gap-3 px-4 py-3">
                <button
                  onClick={() => toggle(branch._id)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  {isOpen(branch._id) ? (
                    <ChevronDown size={16} />
                  ) : (
                    <ChevronRight size={16} />
                  )}
                </button>
                <Building2 size={18} className="text-primary" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-foreground">
                      {branch.name}
                    </span>
                    {branch.isDefault && <DefaultPill />}
                    <KindTag kind={branch.kind} />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {branch.locations.length} location
                    {branch.locations.length !== 1 ? "s" : ""}
                    {branch.unitsOnHand > 0
                      ? ` · ${branch.unitsOnHand} units on hand`
                      : ""}
                  </div>
                </div>
                <RowActions
                  isDefault={branch.isDefault}
                  canDelete={branch.canDelete}
                  onEdit={() =>
                    setDialog({ type: "branch", mode: "edit", node: branch })
                  }
                  onMakeDefault={() => makeDefaultBranch.mutate(branch._id)}
                  onDelete={() => delBranch.mutate(branch._id)}
                  deleteHint={
                    branch.isDefault
                      ? "Can't remove the default branch"
                      : branch.locations.length > 0
                        ? "Remove its locations first"
                        : "Remove branch"
                  }
                />
              </div>

              {/* Locations under this branch */}
              {isOpen(branch._id) && (
                <div className="border-t border-border">
                  {branch.locations.map((loc) => (
                    <div
                      key={loc._id}
                      className="flex items-center gap-3 py-2.5 pl-12 pr-4"
                    >
                      {loc.kind === "warehouse" ? (
                        <Warehouse
                          size={15}
                          className="text-muted-foreground"
                        />
                      ) : (
                        <Store size={15} className="text-muted-foreground" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-foreground">
                            {loc.name}
                          </span>
                          {loc.isDefault && <DefaultPill />}
                          <KindTag kind={loc.kind} />
                        </div>
                        {loc.unitsOnHand > 0 && (
                          <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                            <PackageOpen size={11} /> {loc.unitsOnHand} units on
                            hand
                          </div>
                        )}
                      </div>
                      <RowActions
                        small
                        isDefault={loc.isDefault}
                        canDelete={loc.canDelete}
                        onEdit={() =>
                          setDialog({
                            type: "location",
                            mode: "edit",
                            branchId: branch._id,
                            node: loc,
                          })
                        }
                        onMakeDefault={() =>
                          makeDefaultLocation.mutate(loc._id)
                        }
                        onDelete={() => delLocation.mutate(loc._id)}
                        deleteHint={
                          loc.isDefault
                            ? "Can't remove the default location"
                            : loc.unitsOnHand > 0
                              ? "Transfer its stock out first"
                              : "Remove location"
                        }
                      />
                    </div>
                  ))}
                  <button
                    onClick={() =>
                      setDialog({
                        type: "location",
                        mode: "create",
                        branchId: branch._id,
                      })
                    }
                    className="flex w-full items-center gap-1.5 py-2.5 pl-12 pr-4 text-left text-sm text-primary hover:bg-muted/40"
                  >
                    <Plus size={14} /> Add location to {branch.name}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {dialog && (
        <EditDialog
          dialog={dialog}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            invalidate();
          }}
        />
      )}
    </div>
  );
}

function DefaultPill() {
  return (
    <span className="inline-flex items-center gap-0.5 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
      <Star size={9} className="fill-primary" /> Default
    </span>
  );
}
function KindTag({ kind }: { kind: string }) {
  return (
    <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
      {kind}
    </span>
  );
}

function RowActions({
  isDefault,
  canDelete,
  onEdit,
  onMakeDefault,
  onDelete,
  deleteHint,
  small,
}: {
  isDefault: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onMakeDefault: () => void;
  onDelete: () => void;
  deleteHint: string;
  small?: boolean;
}) {
  const sz = small ? 14 : 15;
  return (
    <div className="flex items-center gap-1">
      {!isDefault && (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          title="Make default"
          onClick={onMakeDefault}
        >
          <Star size={sz} />
        </Button>
      )}
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        title="Edit"
        onClick={onEdit}
      >
        <Pencil size={sz} />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          "h-7 w-7",
          canDelete
            ? "text-muted-foreground hover:text-destructive"
            : "cursor-not-allowed opacity-40",
        )}
        title={deleteHint}
        disabled={!canDelete}
        onClick={() => canDelete && onDelete()}
      >
        <Trash2 size={sz} />
      </Button>
    </div>
  );
}

function EditDialog({
  dialog,
  onClose,
  onSaved,
}: {
  dialog: any;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isBranch = dialog.type === "branch";
  const editing = dialog.mode === "edit";
  const node = dialog.node;
  const [name, setName] = useState(node?.name || "");
  const [code, setCode] = useState(node?.code || "");
  const [kind, setKind] = useState(
    node?.kind || (isBranch ? "outlet" : "store"),
  );
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      if (isBranch) {
        if (editing)
          await api.patch(`/locations/branches/${node._id}`, {
            name,
            code,
            kind,
          });
        else await api.post("/locations/branches", { name, code, kind });
      } else {
        if (editing)
          await api.patch(`/locations/locations/${node._id}`, {
            name,
            code,
            kind,
          });
        else
          await api.post("/locations/locations", {
            branchId: dialog.branchId,
            name,
            code,
            kind,
          });
      }
      toast.success(
        editing ? "Saved" : `${isBranch ? "Branch" : "Location"} added`,
      );
      onSaved();
    } catch (e: any) {
      toast.error(
        e?.response?.data?.error ||
          e?.response?.data?.message ||
          "Couldn't save",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {editing ? "Edit" : "Add"} {isBranch ? "branch" : "location"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label htmlFor="loc-name">Name</Label>
            <Input
              id="loc-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={
                isBranch ? "e.g. Westside Branch" : "e.g. Main Warehouse"
              }
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="loc-code">Code (optional)</Label>
              <Input
                id="loc-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="WEST"
              />
            </div>
            <div className="space-y-1">
              <Label>Type</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(isBranch ? BRANCH_KINDS : LOCATION_KINDS).map((k) => (
                    <SelectItem key={k} value={k}>
                      {k}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving && <Loader2 size={15} className="mr-1.5 animate-spin" />}
            {editing ? "Save changes" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
